<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';
import BillingSectionCard from './BillingSectionCard.vue';
import { formatUsdFromCents, prorateSeatCents } from '../helpers/money';
import { remainingSecondsUntil } from '../helpers/formatBillingTime';

const props = defineProps({
  seats: { type: Object, default: null },
  subscription: { type: Object, default: null },
  catalog: { type: Object, default: null },
  isPro: { type: Boolean, default: false },
  submitting: { type: Boolean, default: false },
});

const emit = defineEmits(['changeSeats']);

const { t } = useI18n();

const currentLimit = computed(
  () => Number(props.seats?.limit ?? props.subscription?.seat_limit) || 5
);
const used = computed(() => Number(props.seats?.used) || 0);
const baseSeats = computed(() => Number(props.catalog?.base_seats) || 5);
const seatCap = computed(() => Number(props.catalog?.seat_cap) || 100);
const seatPrice = computed(
  () => Number(props.catalog?.seat_price_cents) || 500
);
const cycleSeconds = computed(
  () => Number(props.catalog?.cycle_seconds) || 30 * 24 * 60 * 60
);
const pendingLimit = computed(
  () => props.seats?.pending_limit ?? props.subscription?.pending_seat_limit
);

const draftLimit = ref(currentLimit.value);

watch(currentLimit, value => {
  draftLimit.value = value;
});

const delta = computed(() => Number(draftLimit.value) - currentLimit.value);

const estimatedCents = computed(() => {
  if (delta.value <= 0) return 0;
  return prorateSeatCents({
    unitPriceCents: seatPrice.value,
    quantity: delta.value,
    remainingSeconds: remainingSecondsUntil(
      props.subscription?.current_period_ends_at
    ),
    cycleSeconds: cycleSeconds.value,
  });
});

const canSubmit = computed(() => {
  if (!props.isPro) return false;
  if (draftLimit.value === currentLimit.value) return false;
  if (draftLimit.value < used.value) return false;
  if (draftLimit.value < baseSeats.value) return false;
  if (draftLimit.value > seatCap.value) return false;
  return true;
});

const onSubmit = () => {
  if (!canSubmit.value) return;
  emit('changeSeats', {
    order_type: 'seats',
    seat_limit: Number(draftLimit.value),
    amount_cents: estimatedCents.value,
    delta: delta.value,
  });
};
</script>

<template>
  <BillingSectionCard
    :title="t('CUSTOM.BILLING.SEATS.TITLE')"
    :description="t('CUSTOM.BILLING.SEATS.DESCRIPTION')"
  >
    <div class="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
      <div class="rounded-lg bg-n-alpha-1 p-3">
        <div class="text-xs text-n-slate-11">
          {{ t('CUSTOM.BILLING.SEATS.USED_LIMIT') }}
        </div>
        <div class="mt-1 text-lg font-semibold text-n-slate-12">
          {{ used }} / {{ currentLimit }}
        </div>
      </div>
      <div class="rounded-lg bg-n-alpha-1 p-3">
        <div class="text-xs text-n-slate-11">
          {{ t('CUSTOM.BILLING.SEATS.UNIT_PRICE') }}
        </div>
        <div class="mt-1 text-sm font-medium text-n-slate-12">
          {{ formatUsdFromCents(seatPrice) }}
          <span class="text-n-slate-11">{{
            t('CUSTOM.BILLING.SEATS.PER_SEAT')
          }}</span>
        </div>
      </div>
    </div>

    <p
      v-if="pendingLimit != null && pendingLimit !== currentLimit"
      class="mb-3 text-xs text-n-slate-11"
    >
      {{ t('CUSTOM.BILLING.SEATS.PENDING', { limit: pendingLimit }) }}
    </p>

    <template v-if="isPro">
      <div
        class="flex flex-wrap items-end gap-3 rounded-lg border border-n-weak p-3"
      >
        <div class="flex flex-col gap-1">
          <label class="text-xs text-n-slate-11" for="seat-limit-input">
            {{ t('CUSTOM.BILLING.SEATS.NEW_LIMIT') }}
          </label>
          <input
            id="seat-limit-input"
            v-model.number="draftLimit"
            type="number"
            :min="Math.max(baseSeats, used)"
            :max="seatCap"
            step="1"
            class="box-border h-9 w-28 px-2.5 bg-n-alpha-1 border border-n-weak rounded text-sm text-n-slate-12"
          />
        </div>
        <div class="text-sm text-n-slate-11 pb-2">
          <template v-if="delta > 0">
            +{{ delta }} · {{ formatUsdFromCents(estimatedCents) }}
            <span class="block text-xs">{{
              t('CUSTOM.BILLING.SEATS.PRORATED')
            }}</span>
          </template>
          <template v-else-if="delta < 0">
            {{ t('CUSTOM.BILLING.SEATS.DECREASE_HINT') }}
          </template>
          <template v-else>—</template>
        </div>
        <Button
          size="sm"
          color="blue"
          class="ms-auto"
          :label="t('CUSTOM.BILLING.SEATS.APPLY')"
          :is-loading="submitting"
          :disabled="submitting || !canSubmit"
          @click="onSubmit"
        />
      </div>
      <p
        v-if="draftLimit < used"
        class="mb-0 mt-2 text-sm text-n-amber-11"
        role="alert"
      >
        {{ t('CUSTOM.BILLING.GATES.SEAT_BELOW_USED') }}
      </p>
    </template>
    <p v-else class="mb-0 text-sm text-n-slate-11">
      {{ t('CUSTOM.BILLING.SEATS.FREE_FIXED') }}
    </p>
  </BillingSectionCard>
</template>
